import { z } from 'zod';
import { CATALOGUE_ENTITIES, catalogueEntry } from './query-catalogue.js';
import { reachableFrom } from './query-catalogue.js';
/**
 * G5 — provenance is FOUR layers, never collapsed.
 *
 * | Layer | Answers |
 * | --- | --- |
 * | Routing | How was the question interpreted? |
 * | Computation | What approved mechanism produced the figures? |
 * | Evidence | Which stored facts support this? |
 * | Raw lineage | Where did the underlying data arrive from? |
 *
 * ⚠️ **A query composed by a model and executed by the server is COMPUTED, and
 * must still say a model composed it.** Collapsing the first two layers is how
 * "a model was involved" becomes "a model produced the number", or the reverse —
 * and they are opposite facts about how much a reader should trust it.
 *
 * ⚠️ **`source` is NOT a telematics vendor.** The review that proposed
 * `source = AJNAVIEW` would have been false for most answers: trips are derived,
 * incidents are correlated, a tenant may run several providers, and a figure
 * spanning branches spans their connections too. Provenance says what OIP
 * computed the answer FROM.
 */
/* ───────────────────────── layer 1 — routing ─────────────────────────────── */
/**
 * How the question was interpreted.
 *
 * ⚠️ **Declared HERE rather than in `copilot.ts`, and the move fixed a real
 * import cycle.** It is G5's first layer, so it belongs with the other three;
 * it lived beside the copilot's finding shape because that was the first thing
 * to need it. `copilot.ts` re-exports it under its old name, so every existing
 * caller is untouched — and the copilot can now name a query document as a
 * capability's parameters without `copilot → query-document → query-provenance →
 * copilot` closing a loop at runtime.
 *
 * DISCLOSURE, never authorization: it changes what the provenance says and
 * nothing about what may run, so a caller misreporting it gains nothing.
 *
 * The four values and why `COMPOSED` is not `MODEL` are documented on the
 * constant below.
 */
/*
 * ⚠️ **`COMPOSED` IS A STRONGER CLAIM THAN `MODEL` AND MUST NOT SHARE ITS LABEL.**
 *
 * - `DIRECT` — the reader clicked the question. Nothing was interpreted.
 * - `MATCHED` — resolved from typed text by the deterministic matcher.
 * - `MODEL` — the matcher could not decide, and a model CHOSE one of the declared
 *   questions. The question answered is still one somebody wrote.
 * - `COMPOSED` — a model BUILT the question, filling a `QueryDocument` from the
 *   catalogue. Nobody wrote this question before it was asked.
 *
 * The difference is what a reader has to check. Under `MODEL` the risk is that the
 * model picked the wrong declared question, and the declared question is on screen
 * to be compared against. Under `COMPOSED` there is no declared question to
 * compare against — the grain, the dimensions, the measures and the filters were
 * all chosen — so the reader must be able to see the document itself.
 *
 * Folding it into `MODEL` would tell a reader a model interpreted their sentence
 * while hiding that it also decided what was measured. G5 separates who COMPOSED
 * from who COMPUTED; this is that separation inside the routing layer.
 *
 * ⚠️ Still DISCLOSURE, never authorization — see below. A caller claiming
 * `COMPOSED` gains nothing, because what may run is decided by
 * `validateRoutedCapability` and the query validator, not by this field.
 */
export const QUERY_ROUTING = ['DIRECT', 'MATCHED', 'MODEL', 'COMPOSED'];
export const queryRoutingSchema = z.enum(QUERY_ROUTING);
/* ─────────────────────── layer 2 — computation ───────────────────────────── */
/**
 * The approved mechanism behind the figures.
 *
 * Both values are declared although this surface only ever produces the second:
 * `CAPABILITY` is what a copilot finding carries, and one vocabulary across both
 * is the point — a reader comparing a capability answer with a query answer must
 * be reading the same word for the same thing.
 */
export const QUERY_COMPUTATION = ['CAPABILITY', 'DECLARED_QUERY'];
export const queryComputationSchema = z.enum(QUERY_COMPUTATION);
/* ────────────────────── layer 4 — raw lineage ────────────────────────────── */
/**
 * Where the records behind an answer came from.
 *
 * ⚠️ **A VEHICLE IS NOT INGESTED DATA**, and this is the distinction that makes
 * the layer worth having. Ingestion never creates a vehicle — a mistyped
 * registration would become a phantom one — so a vehicle and a driver are typed
 * by a person, and telling a reader their vehicle count "came from your
 * provider" would be false. An incident is neither: OIP derived it by
 * correlating events that DID arrive from a provider.
 *
 * Three answers, because a reader acts differently on each: a figure they can
 * fix by editing a record, one they can fix by chasing a device, and one that is
 * the platform's own arithmetic.
 */
export const DATA_LINEAGE = [
    'ENTERED_BY_A_PERSON',
    'REPORTED_BY_A_DEVICE',
    'DERIVED_BY_OIP',
];
export const dataLineageSchema = z.enum(DATA_LINEAGE);
/**
 * Declared per entity, beside its grain and its evidence.
 *
 * ⚠️ Kept HERE rather than inferred from the model name, because the difference
 * between entered and reported is a fact about how the platform works, not about
 * a table. `Vehicle` and `VehicleTelemetryHourly` are both rows in the same
 * database and a reader must not be told they arrived the same way.
 */
export const ENTITY_LINEAGE = {
    vehicle: {
        kind: 'ENTERED_BY_A_PERSON',
        note: 'Vehicles are created by somebody on your team. Ingestion never creates one, so a vehicle missing here is one nobody has added.',
        inboundTrail: null,
    },
    driver: {
        kind: 'ENTERED_BY_A_PERSON',
        note: 'Drivers are created by somebody on your team.',
        inboundTrail: null,
    },
    'vehicle-hour': {
        kind: 'REPORTED_BY_A_DEVICE',
        note: 'Readings are what the vehicle reported, grouped into hours by OIP. An hour with no reading is absent rather than zero.',
        inboundTrail: '/admin/inbound-events',
    },
    /*
     * ⚠️ **The PUREST `REPORTED_BY_A_DEVICE` case**, and worth distinguishing from
     * the rollup beside it: a rollup is device readings that OIP bucketed into
     * hours, while an event is the device's own report mapped into OIP's
     * vocabulary and stored. Both came off a vehicle; only one has OIP's
     * arithmetic in it.
     */
    event: {
        kind: 'REPORTED_BY_A_DEVICE',
        note: 'Events are what your provider reported, mapped into OIP’s own vocabulary. The payload they sent is kept verbatim.',
        inboundTrail: '/admin/inbound-events',
    },
    incident: {
        kind: 'DERIVED_BY_OIP',
        note: 'Incidents are raised by your correlation rules from events a provider sent. OIP computed the grouping; the provider sent the events.',
        inboundTrail: '/admin/inbound-events',
    },
    trip: {
        kind: 'DERIVED_BY_OIP',
        note: 'Trips are derived by OIP from telemetry it already held. No provider sends a trip — theirs would carry different rules about what splits a journey.',
        inboundTrail: '/admin/inbound-events',
    },
    recommendation: {
        kind: 'DERIVED_BY_OIP',
        note: 'Recommendations are produced by your decision policies from incidents OIP raised.',
        inboundTrail: '/admin/inbound-events',
    },
};
/* ──────────────────────────── the wire shape ─────────────────────────────── */
export const queryWindowSchema = z
    .object({
    /** Resolved to ABSOLUTE instants — see the note on `provenanceFor`. */
    from: z.string().datetime(),
    to: z.string().datetime(),
    hours: z.number().int().positive(),
})
    .strict();
export const queryLineageSchema = z
    .object({
    entity: z.string(),
    kind: dataLineageSchema,
    note: z.string(),
    inboundTrail: z.string().nullable(),
})
    .strict();
export const queryProvenanceSchema = z
    .object({
    /* ── layer 1 — routing ── */
    routing: queryRoutingSchema,
    /**
     * What the reader typed, when it was interpreted. Null for a document built
     * by a control.
     *
     * ⚠️ Echoed back to the caller who sent it and STORED NOWHERE. D17 — "the
     * question a person typed is theirs" — and a stateless answer has no second
     * reader for it to leak to. The moment a query answer becomes a stored
     * finding, this must be redacted for anybody but its author, exactly as the
     * copilot's `routedFrom` already is.
     */
    routedFrom: z.string().nullable(),
    /* ── layer 2 — computation ── */
    computation: queryComputationSchema,
    /**
     * What was actually run, in the catalogue's own words.
     *
     * ⚠️ The point of the layer: a figure is checkable only if the reader can
     * see the question that produced it. Not the document echoed back — the
     * LABELS, so a reader who never learned the key names can still read it.
     */
    grain: z.string(),
    grainLabel: z.string(),
    dimensions: z.array(z.object({ key: z.string(), label: z.string() }).strict()),
    measures: z.array(z.object({ key: z.string(), label: z.string(), aggregation: z.string() }).strict()),
    filters: z.array(z.object({ key: z.string(), label: z.string(), operator: z.string() }).strict()),
    window: queryWindowSchema.nullable(),
    /* ── scope: never an unqualified "the fleet" ── */
    scopeLabel: z.string(),
    scopeTenantIds: z.array(z.string().uuid()),
    asOf: z.string().datetime(),
    /* ── layer 3 — evidence ── */
    evidence: z
        .object({
        entity: z.string(),
        /** One row of this IS what? */
        grain: z.string(),
        /** How an answer at this grain stays checkable. */
        trail: z.string(),
        /** Source records folded into the answer. */
        records: z.number().int().nonnegative(),
    })
        .strict(),
    /* ── layer 4 — raw lineage ── */
    lineage: z.array(queryLineageSchema),
})
    .strict();
/**
 * Build the four layers for an executed document.
 *
 * ⚠️ **The window is resolved to ABSOLUTE INSTANTS.** `windowHours: 168` is
 * relative to the moment it ran, so an answer that recorded only the number
 * describes a different week every time it is read — and a finding is a SNAPSHOT
 * (D22). The hours are kept beside them because that is what somebody asked for,
 * and the instants are what they got.
 *
 * ⚠️ **Lineage covers every entity the answer TOUCHED**, not only its grain. A
 * query at the incident grain grouped by vehicle class reads one thing a
 * provider sent and one thing a person typed, and reporting only the first would
 * tell a reader their unclassified vehicles were a device problem.
 */
export function provenanceFor(document, context) {
    const grain = document.grain;
    const entity = CATALOGUE_ENTITIES[grain];
    const label = (key) => catalogueEntry(key)?.label ?? key;
    const touched = new Set([grain]);
    for (const key of [
        ...document.dimensions,
        ...document.measures.map((m) => m.field),
        ...document.filters.map((f) => f.field),
    ]) {
        const e = catalogueEntry(key);
        if (e && reachableFrom(grain).includes(e.entity))
            touched.add(e.entity);
    }
    return {
        routing: context.routing,
        routedFrom: context.routedFrom,
        computation: 'DECLARED_QUERY',
        grain,
        grainLabel: entity.grain,
        dimensions: document.dimensions.map((key) => ({ key, label: label(key) })),
        measures: document.measures.map((m) => ({
            key: m.field,
            label: label(m.field),
            aggregation: m.aggregation,
        })),
        filters: document.filters.map((f) => ({
            key: f.field,
            label: label(f.field),
            operator: f.operator,
        })),
        window: document.windowHours === undefined
            ? null
            : {
                from: new Date(context.asOf.getTime() - document.windowHours * 3_600_000).toISOString(),
                to: context.asOf.toISOString(),
                hours: document.windowHours,
            },
        scopeLabel: context.scopeLabel,
        scopeTenantIds: [...context.scopeTenantIds],
        asOf: context.asOf.toISOString(),
        evidence: {
            entity: grain,
            grain: entity.grain,
            trail: entity.evidence,
            records: context.records,
        },
        lineage: [...touched].map((e) => ({
            entity: e,
            kind: ENTITY_LINEAGE[e].kind,
            note: ENTITY_LINEAGE[e].note,
            inboundTrail: ENTITY_LINEAGE[e].inboundTrail,
        })),
    };
}
export const EVIDENCE_PROJECTIONS = {
    incident: {
        select: ['id', 'title', 'incidentType', 'riskLevel', 'status', 'openedAt'],
        labelField: 'title',
        atField: 'openedAt',
        path: (id) => `/incidents/${id}`,
        idField: 'id',
    },
    vehicle: {
        select: ['id', 'name', 'registration', 'status'],
        labelField: 'name',
        atField: null,
        path: (id) => `/vehicles/${id}`,
        idField: 'id',
    },
    driver: {
        /*
         * ⚠️ No licence number. It is a personal identifier and naming the driver is
         * what a reader needs — the same restraint the incident timeline shows when
         * it says "Assigned to an operator" rather than inventing a name.
         */
        select: ['id', 'name', 'status'],
        labelField: 'name',
        atField: null,
        path: (id) => `/drivers/${id}`,
        idField: 'id',
    },
    event: {
        /*
         * ⚠️ No `measurements` and no `location`. Both are JSON the catalogue
         * deliberately refuses (§8 of the register): the keys present depend on the
         * event type, so a measure over them is defined only within one type. An
         * evidence projection that leaked them would be a way to read fields nobody
         * admitted.
         */
        select: ['id', 'eventType', 'severity', 'occurredAt'],
        labelField: 'eventType',
        atField: 'occurredAt',
        /* An event has no page of its own; it is read on its incident. */
        path: null,
        idField: 'id',
    },
    trip: {
        select: ['id', 'startedAt', 'endedAt', 'distanceMetres', 'distanceSource'],
        labelField: 'distanceSource',
        atField: 'startedAt',
        /* A trip has no page of its own; it is read on its vehicle. */
        path: null,
        idField: 'id',
    },
    recommendation: {
        select: ['id', 'recommendedAction', 'actionCategory', 'status', 'createdAt'],
        labelField: 'recommendedAction',
        atField: 'createdAt',
        path: (id) => `/recommendations/${id}`,
        idField: 'id',
    },
    'vehicle-hour': {
        /*
         * A rollup row has no id — its key is the tenant, the vehicle and the hour.
         * `vehicleId` identifies what a reader can open; the hour says which one.
         */
        select: ['vehicleId', 'hour', 'sampleCount'],
        labelField: 'vehicleId',
        atField: 'hour',
        path: (id) => `/vehicles/${id}`,
        idField: 'vehicleId',
    },
};
export const queryEvidenceRowSchema = z
    .object({
    id: z.string(),
    label: z.string(),
    at: z.string().datetime().nullable(),
    /** Where to open it, or null where the platform has no page for this record. */
    href: z.string().nullable(),
    /** The projected columns, exactly as declared. */
    fields: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])),
})
    .strict();
export const queryEvidenceResponseSchema = z
    .object({
    rows: z.array(queryEvidenceRowSchema),
    /**
     * How many records the figure folded, counted by THIS read.
     *
     * ⚠️ Counted rather than taken from the answer the reader is drilling into:
     * that figure was true at its own `asOf`, and a claim of "20 of 65" built
     * from two different reads is a number nobody can reproduce.
     */
    total: z.number().int().nonnegative(),
    truncated: z.boolean(),
    asOf: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=query-provenance.js.map